import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Department,
  TICKET_PRIORITY_ORDER,
  type CreateJobBody,
  type EditJobBody,
  type JobEditView,
  type TicketPriority,
  departmentLabelKey,
  ticketPriorityLabelKey,
} from '@nbh/shared';
import { adminApi, type LocationNode } from '../../lib/adminApi';
import { fromDateTimeLocal, toDateTimeLocal } from '../../lib/format';

/** Flattened for a `<select>`: the tree is for browsing, a dropdown needs one list. */
export interface PlaceOption {
  id: string;
  label: string;
}

export function flattenPlaces(nodes: LocationNode[], prefix = ''): PlaceOption[] {
  return nodes.flatMap((node) => {
    const label = prefix ? `${prefix} › ${node.name}` : node.name;
    // Retired places are omitted, not disabled: the server refuses them, and offering
    // one only to reject it wastes a round trip and an operator's patience.
    const self = node.isActive ? [{ id: node.id, label }] : [];

    return [...self, ...flattenPlaces(node.children, label)];
  });
}

const FIELD =
  'mt-1 min-h-[48px] w-full rounded border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100';

/** A labelled select. Three of these in a row is most of this form. */
function SelectField({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="mt-3 block text-xs text-slate-400">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className={FIELD}>
        {children}
      </select>
    </label>
  );
}

/** A labelled text input, or a textarea when `multiline`. */
function TextField({
  label,
  value,
  onChange,
  maxLength,
  multiline = false,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  maxLength: number;
  multiline?: boolean;
}) {
  const shared = {
    value,
    maxLength,
    onChange: (event: { target: { value: string } }) => onChange(event.target.value),
  };

  return (
    <label className="mt-3 block text-xs text-slate-400">
      {label}
      {multiline ? (
        <textarea {...shared} rows={3} className={`${FIELD} py-2`} />
      ) : (
        <input {...shared} className={FIELD} />
      )}
    </label>
  );
}

/** Submit and cancel. Full-width submit, in thumb reach at the foot of the sheet. */
function FormActions({
  ready,
  busy,
  onCancel,
}: {
  ready: boolean;
  busy: boolean;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="mt-4 flex gap-2">
      <button
        type="submit"
        disabled={!ready}
        className="min-h-[48px] flex-1 rounded-lg bg-brookfields-green ring-1 ring-brookfields-gold/70 text-sm font-semibold text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
      >
        {busy ? t('superAdmin.jobs.saving') : t('superAdmin.jobs.save')}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="min-h-[48px] rounded-lg bg-transparent px-4 text-sm text-slate-400"
      >
        {t('superAdmin.cancel')}
      </button>
    </div>
  );
}

/**
 * The deadline, in property time.
 *
 * A bare `datetime-local` would read and write the device's zone, which is the one thing
 * `format.ts` exists to stop — the conversion happens at the edges, in the caller.
 */
function DueAtField({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const { t } = useTranslation();

  return (
    <label className="mt-3 block text-xs text-slate-400">
      {t('superAdmin.jobs.dueAt')}
      <input
        type="datetime-local"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={FIELD}
      />
      <span className="mt-1 block text-[11px] text-slate-500">
        {t('superAdmin.jobs.dueAtHint')}
      </span>
    </label>
  );
}

interface Fields {
  title: string;
  description: string;
  priority: TicketPriority;
  department: Department;
  locationId: string;
  assigneeId: string;
  dueAt: string;
}

/**
 * What both requests share.
 *
 * An empty select is "none", which is a real answer — not every job is tied to a room or
 * a named person. A blank deadline is *omitted* rather than nulled: the column is NOT
 * NULL, and on create leaving it out is what lets the SLA policy set it from priority.
 */
function sharedBody(fields: Fields) {
  const due = fromDateTimeLocal(fields.dueAt);

  return {
    title: fields.title.trim(),
    priority: fields.priority,
    department: fields.department,
    locationId: fields.locationId || null,
    assigneeId: fields.assigneeId || null,
    ...(due === null ? {} : { dueAt: new Date(due) }),
  };
}

const createBody = (fields: Fields): CreateJobBody => ({
  ...sharedBody(fields),
  description: fields.description.trim() || undefined,
});

// Description is nullable on edit: clearing the box means clearing the field, which is
// the one place `null` and `undefined` have to differ on this form.
const editBody = (fields: Fields): EditJobBody => ({
  ...sharedBody(fields),
  description: fields.description.trim() || null,
});

interface JobFormProps {
  /**
   * Null when raising a new job; the fetched editable view when correcting one.
   *
   * Fetched rather than taken from the queue row on purpose: the row carries
   * `locationName` and `assigneeName`, and a select is prefilled by id.
   */
  job: JobEditView | null;
  places: PlaceOption[];
  staff: { id: string; name: string }[];
  onSaved: () => void;
  onCancel: () => void;
}

/**
 * Raise a job, or correct one.
 *
 * One form for both because the fields are the same and the difference is only which
 * request it sends. Status is deliberately absent: overriding it skips the lifecycle
 * guards, so it lives on its own control with its own mandatory reason.
 */
export function SuperAdminJobForm({ job, places, staff, onSaved, onCancel }: JobFormProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(job?.title ?? '');
  const [description, setDescription] = useState(job?.description ?? '');
  const [priority, setPriority] = useState<TicketPriority>(job?.priority ?? 'MEDIUM');
  const [department, setDepartment] = useState<Department>(
    job?.department ?? Department.Maintenance,
  );
  const [locationId, setLocationId] = useState(job?.locationId ?? '');
  const [assigneeId, setAssigneeId] = useState(job?.assigneeId ?? '');
  // Property time, not the device's — see `toDateTimeLocal`. Blank when raising a job,
  // because the SLA policy answers it better than a guessed default would.
  const [dueAt, setDueAt] = useState(job ? toDateTimeLocal(job.dueAt) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = title.trim().length >= 3 && !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;

    setBusy(true);
    setError(null);
    try {
      const fields = { title, description, priority, department, locationId, assigneeId, dueAt };

      if (job) await adminApi.superAdmin.jobs.edit(job.id, editBody(fields));
      else await adminApi.superAdmin.jobs.create(createBody(fields));

      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('superAdmin.failed'));
      setBusy(false);
    }
  }

  const heading = job
    ? t('superAdmin.jobs.editHeading', { subject: `#${job.reference}` })
    : t('superAdmin.jobs.createHeading');

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-3 sm:items-center">
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-label={heading}
        className="max-h-full w-full max-w-md overflow-y-auto rounded-xl border border-slate-700 bg-slate-950 p-4"
      >
        <p className="text-sm font-semibold text-slate-100">{heading}</p>

        <TextField
          label={t('superAdmin.jobs.title')}
          value={title}
          onChange={setTitle}
          maxLength={160}
        />

        <TextField
          label={t('superAdmin.jobs.description')}
          value={description}
          onChange={setDescription}
          maxLength={4000}
          multiline
        />

        <SelectField
          label={t('superAdmin.jobs.priority')}
          value={priority}
          onChange={(next) => setPriority(next as TicketPriority)}
        >
          {TICKET_PRIORITY_ORDER.map((level) => (
            <option key={level} value={level}>
              {t(ticketPriorityLabelKey[level])}
            </option>
          ))}
        </SelectField>

        <SelectField
          label={t('superAdmin.jobs.department')}
          value={department}
          onChange={(next) => setDepartment(next as Department)}
        >
          {Object.values(Department).map((value) => (
            <option key={value} value={value}>
              {t(departmentLabelKey[value])}
            </option>
          ))}
        </SelectField>

        <SelectField label={t('superAdmin.jobs.place')} value={locationId} onChange={setLocationId}>
          <option value="">{t('superAdmin.jobs.noPlace')}</option>
          {places.map((place) => (
            <option key={place.id} value={place.id}>
              {place.label}
            </option>
          ))}
        </SelectField>

        <SelectField
          label={t('superAdmin.jobs.assignee')}
          value={assigneeId}
          onChange={setAssigneeId}
        >
          <option value="">{t('superAdmin.jobs.unassigned')}</option>
          {staff.map((person) => (
            <option key={person.id} value={person.id}>
              {person.name}
            </option>
          ))}
        </SelectField>

        <DueAtField value={dueAt} onChange={setDueAt} />

        {error && (
          <p role="alert" className="mt-2 text-sm text-red-400">
            {error}
          </p>
        )}

        <FormActions ready={ready} busy={busy} onCancel={onCancel} />
      </form>
    </div>
  );
}
